import type { AuthenticatedUser } from '@src/features/users';
import { jobTitleFromDto } from '@src/features/users/lib/jobtitle';
import { shortUserFromDto } from '@src/features/users/lib/user';
import { teamFromDto } from '@src/features/users/lib/team';
import { workingTimeFromDto } from '@src/features/users/lib/workingtime';
import { isoWeekday } from '@src/shared/lib';
import type { ResourceUser, ResourceUserDto, VacationBalances } from '../types';

export function resourceUserFromDto(user: ResourceUserDto): ResourceUser {
  return {
    id: user.id,
    email: user.email,
    name: user.name,
    firstName: user.user_first_name,
    familyName: user.user_family_name,
    language: user.user_language,
    manager: user.user_user_manager
      ? shortUserFromDto(user.user_user_manager)
      : null,
    hrId: user.user_user_hr,
    branch: user.user_branch,
    jobTitle: user.user_job_title ? jobTitleFromDto(user.user_job_title) : null,
    team: user.user_team ? teamFromDto(user.user_team) : null,
    portrait: user.user_portrait,
    salaryPercentage: user.salary_percentage,
    worktime: user.worktime.map((w) => workingTimeFromDto(w)),
    rankSort: user.rank_sort,
  };
}
export function userResourceId(
  userId: number | null | undefined,
): string | undefined {
  if (userId) return `user-${userId}`;
}

type BusinessHours = {
  daysOfWeek: number[];
  startTime: string;
  endTime: string;
};
type Resource = {
  id: string;
  title: string;
  businessHours: BusinessHours[];
  manager: number | null;
  jobTitle: string | null;
  salaryPercentage: number | null;
  portrait: string | null;
  order: number;
  rankSort: number;
  userId: number;
  team: string;
  vacationUsed: number;
  vacationAvailable: number;
};

function getBuisnessHoursForResrouce(resource: ResourceUser): BusinessHours[] {
  let buisinessHours: BusinessHours[] = [];
  if (!resource.worktime || !resource.worktime.length) {
    return buisinessHours;
  }
  const toMinutes = (t: string) => {
    if (!t) {
      return null;
    }
    const [hh, mm] = t.split(':').map(Number);
    return hh * 60 + mm;
  };

  const fromMinutes = (m: number) => {
    const h = Math.floor(m / 60);
    const min = m % 60;
    return `${h}:${min.toString().padStart(2, '0')}`;
  };

  const intervals: Array<{ start: number; end: number; day: number }> = [];
  for (const row of resource.worktime || []) {
    const day = row.weekDay;

    const ms = toMinutes(row.starTimeMorning);
    const me = toMinutes(row.endTimeMorning);
    if (ms != null && me != null && me > ms) {
      intervals.push({ start: ms, end: me, day });
    }

    const as = toMinutes(row.startTimeAfternoon);
    const ae = toMinutes(row.endTimeAfternoon);
    if (as != null && ae != null && ae > as) {
      intervals.push({ start: as, end: ae, day });
    }
  }

  const map: Map<string, { start: number; end: number; days: Set<number> }> =
    new Map();
  for (const it of intervals) {
    const key = `${it.start}-${it.end}`;
    if (!map.has(key)) {
      map.set(key, { start: it.start, end: it.end, days: new Set() });
    }
    const entry = map.get(key);
    if (entry) entry.days.add(it.day);
  }

  buisinessHours = Array.from(map.values()).map(({ start, end, days }) => ({
    startTime: fromMinutes(start),
    endTime: fromMinutes(end),
    daysOfWeek: Array.from(days).sort((a: number, b: number) => a - b),
  }));

  buisinessHours.sort((a, b) => {
    const sa = a.startTime.split(':').reduce((h, i) => h * 60 + Number(i), 0);
    const sb = b.startTime.split(':').reduce((h, i) => h * 60 + Number(i), 0);
    if (sa !== sb) {
      return sa - sb;
    }
    const ea = a.endTime.split(':').reduce((h, i) => h * 60 + Number(i), 0);
    const eb = b.endTime.split(':').reduce((h, i) => h * 60 + Number(i), 0);
    return ea - eb;
  });
  return buisinessHours;
}

export function usersToCalendarResources(
  users: ResourceUser[],
  currentUser: AuthenticatedUser | null,
  balances: VacationBalances = new Map(),
) {
  if (!users?.length) {
    return [];
  }
  const currentUserId = currentUser?.id;
  const currentManagerId = currentUser?.manager?.id ?? null;

  const currentUserRecord = users.find((u) => u.id === currentUserId);
  const myTeamId = currentUserRecord?.team?.id ?? null;

  const directSubIds = new Set<number>();
  for (const u of users) {
    if (u.manager?.id === currentUserId && u.id !== currentUserId) {
      directSubIds.add(u.id);
    }
  }

  const managedTeamMap = new Map<number, { sort: number; name: string }>();
  for (const u of users) {
    if (
      directSubIds.has(u.id) &&
      u.team &&
      u.team.id !== myTeamId &&
      !managedTeamMap.has(u.team.id)
    ) {
      managedTeamMap.set(u.team.id, { sort: u.team.sort, name: u.team.name });
    }
  }
  const managedTeamsSorted = [...managedTeamMap.entries()].sort(
    (a, b) => a[1].sort - b[1].sort,
  );

  const allTeams = new Map<number, { sort: number; name: string }>();
  for (const u of users) {
    if (u.team && !allTeams.has(u.team.id)) {
      allTeams.set(u.team.id, { sort: u.team.sort, name: u.team.name });
    }
  }

  const specialTeamIds = new Set<number>();
  if (myTeamId) specialTeamIds.add(myTeamId);
  for (const [id] of managedTeamsSorted) specialTeamIds.add(id);

  const remainingTeams = [...allTeams.entries()]
    .filter(([id]) => !specialTeamIds.has(id))
    .sort((a, b) => a[1].sort - b[1].sort);

  let groupIdx = 1;
  const teamGroupIndex = new Map<number, number>();
  const pad = (n: number) => String(n).padStart(4, '0');

  if (myTeamId) {
    teamGroupIndex.set(myTeamId, groupIdx++);
  }
  for (const [id] of managedTeamsSorted) {
    teamGroupIndex.set(id, groupIdx++);
  }
  const directReportsIdx = groupIdx++;
  const teamlessIdx = groupIdx++;
  for (const [id] of remainingTeams) {
    teamGroupIndex.set(id, groupIdx++);
  }

  const getTitle = (item: ResourceUser) =>
    item.familyName && item.firstName
      ? `${item.familyName}, ${item.firstName}`
      : item.name;

  return users.map((item: ResourceUser): Resource => {
    const isCurrentUser = item.id === currentUserId;
    const isManager =
      item.id === currentManagerId && currentManagerId !== currentUserId;

    let team: string;
    if (isCurrentUser || isManager) {
      team = '';
    } else if (item.team && teamGroupIndex.has(item.team.id)) {
      const idx = teamGroupIndex.get(item.team.id)!;
      team = `${pad(idx)}:${item.team.name}`;
    } else if (!item.team && directSubIds.has(item.id)) {
      team = `${pad(directReportsIdx)}:No Team`;
    } else if (!item.team) {
      team = `${pad(teamlessIdx)}:No Team`;
    } else {
      const idx = teamGroupIndex.get(item.team.id) ?? groupIdx;
      team = `${pad(idx)}:${item.team.name}`;
    }

    return {
      id: `user-${String(item.id)}`,
      title: getTitle(item),
      businessHours: getBuisnessHoursForResrouce(item),
      manager: item.manager?.id ?? null,
      jobTitle: item.jobTitle?.title ?? null,
      portrait: item.portrait,
      salaryPercentage: item.salaryPercentage,
      order: isCurrentUser ? 0 : isManager ? 1 : 2,
      rankSort: item.rankSort,
      userId: item.id,
      team,
      vacationUsed: balances.get(item.id)?.used ?? 0,
      vacationAvailable: balances.get(item.id)?.available ?? 0,
    };
  });
}

export function getUserWorktimeForDate(
  userId: number,
  users: ResourceUser[],
  date: string,
) {
  if (!userId || !users || !users.length || !date) {
    return null;
  }

  const user = users.find((u) => u.id === userId);
  if (!user || !user.worktime || !user.worktime.length) {
    return null;
  }

  const dayNumber = isoWeekday(date);
  const dayWorkingTime = user.worktime.find((w) => w.weekDay === dayNumber);
  if (!dayWorkingTime) {
    return null;
  }
  const startTime = dayWorkingTime.starTimeMorning;
  const endTime = dayWorkingTime.endTimeAfternoon;
  return { start: startTime, end: endTime };
}

export function getManagedAbsenceUsers(
  users: ResourceUser[],
  currentUser: AuthenticatedUser | null,
) {
  if (!currentUser) {
    return [];
  }

  if (currentUser.isAdmin) {
    return users;
  }

  return users.filter(
    (user) =>
      user.id === currentUser.id ||
      user.manager?.id === currentUser.id ||
      user.hrId === currentUser.id,
  );
}

export function canManageAbsenceUser(
  targetUserId: number | null | undefined,
  users: ResourceUser[],
  currentUser: AuthenticatedUser | null,
) {
  if (!currentUser || !targetUserId) {
    return false;
  }

  if (currentUser.isAdmin) {
    return true;
  }

  if (targetUserId === currentUser.id) {
    return true;
  }

  return users.some(
    (user) =>
      user.id === targetUserId &&
      (user.manager?.id === currentUser.id || user.hrId === currentUser.id),
  );
}
